// #2866 -- which of a set of classes already carry at least one score sheet.
// The bulk paper-scoring warning (BulkAssignScribesDialog) names these
// classes: paperEntry is stamped onto a ScoreSheet only at CREATION
// (score-api's isPaperEntryClass/createScoreSheet), so flipping it never
// changes a sheet that already exists.
//
// Same per-class check ClassDetailDrawer's single-class warning does (rides
// for the class, then their sheets, filtered through authoritativeSheetsOnly
// so a lone #2815 contender is never counted as "existing sheets", #2974).
// Only runs while `enabled`, so the dialog does not fetch until the operator
// actually touches the paper field.
import { useEffect, useMemo, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { authoritativeSheetsOnly } from "../util/contenderCompare";

export default function useClassesWithSheets(classIds, enabled) {
  const scoreApi = useScoreApi();
  // Stable key so a fresh array identity per render does not refetch.
  const key = useMemo(() => [...new Set(classIds || [])].sort().join("|"), [classIds]);
  const [state, setState] = useState({ key: null, ids: [], error: false });

  useEffect(() => {
    if (!enabled || !key) return undefined;
    let cancelled = false;
    const ids = key.split("|");
    Promise.all(
      ids.map((id) =>
        scoreApi.rides
          .listRides(id)
          .then(unwrap)
          .then((rides) => {
            const rideIds = (rides || []).map((r) => r.id).filter(Boolean);
            if (!rideIds.length) return { id, has: false };
            return scoreApi.scoreSheets
              .listScoreSheetsForRides({ rideIds })
              .then(unwrap)
              .then((sheets) => ({ id, has: authoritativeSheetsOnly(sheets).length > 0 }));
          })
          .catch(() => ({ id, has: false, failed: true })),
      ),
    ).then((results) => {
      if (cancelled) return;
      setState({
        key,
        ids: results.filter((r) => r.has).map((r) => r.id),
        error: results.some((r) => r.failed),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, key, scoreApi]);

  const ready = enabled && state.key === key;
  return {
    loading: enabled && !!key && !ready,
    classIdsWithSheets: ready ? state.ids : [],
    error: ready ? state.error : false,
  };
}
